import { useLazyQuery, useMutation } from "@apollo/client";
import {
  ActivityByIdDocument,
  CreateActivityDocument,
  CreateActivityInput,
  DeleteActivityDocument,
  DocumentsByUserOrganizationIdDocument,
  EventDetailsDocument,
  OrganizationDocumentsDocument,
  ProjectDetailsDocument,
  SignedUrlToUploadFileDocument,
  UpdateActivityDocument,
  UpdateActivityInput,
} from "../../../../../../../../../__generated__/graphql";
import toast from "react-hot-toast";
import type { ParticipantOption } from "../components/ParticipantsSelectInput";

interface UseActivityMutationsParams {
  eventId?: string;
  projectId?: string;
  defaultActivityId?: string;
  participantOptions: ParticipantOption[];
  onSuccess: () => void;
}

export function useActivityMutations({
  eventId,
  projectId,
  defaultActivityId,
  participantOptions,
  onSuccess,
}: UseActivityMutationsParams) {
  const [createActivity, { loading: isCreating }] = useMutation(
    CreateActivityDocument
  );
  const [updateActivity, { loading: isUpdating }] = useMutation(
    UpdateActivityDocument
  );
  const [deleteActivity, { loading: isDeleting }] = useMutation(
    DeleteActivityDocument
  );
  const [uploadDocuments, { loading: isUploading }] = useLazyQuery(
    SignedUrlToUploadFileDocument
  );

  const isSubmitting = isCreating || isUpdating || isUploading || isDeleting;

  const buildRefetchQueries = () => {
    const queries = [
      ...(eventId
        ? [
            {
              query: EventDetailsDocument,
              variables: {
                eventId,
              },
            },
          ]
        : []),
      ...(projectId
        ? [
            {
              query: ProjectDetailsDocument,
              variables: {
                projectId,
              },
            },
          ]
        : []),
      ...(defaultActivityId
        ? [
            {
              query: ActivityByIdDocument,
              variables: {
                activityId: defaultActivityId,
              },
            },
          ]
        : []),
      {
        query: OrganizationDocumentsDocument,
      },
      ...(participantOptions.length > 0
        ? [
            ...participantOptions.map((option) => ({
              query: DocumentsByUserOrganizationIdDocument,
              variables: {
                userOrganizationId: option.userOrganizationId,
              },
            })),
          ]
        : []),
    ];

    return queries;
  };

  const handleCreate = async (input: CreateActivityInput) => {
    try {
      await createActivity({
        variables: {
          createActivityInput: input,
        },
        refetchQueries: buildRefetchQueries(),
      });
      toast.success("Activité créée avec succès");
      onSuccess();
    } catch (error) {
      console.error("Error creating activity:", error);
      toast.error("Une erreur est survenue lors de la création de l'activité");
      throw error;
    }
  };

  const handleUpdate = async (input: UpdateActivityInput) => {
    try {
      await updateActivity({
        variables: {
          updateActivityInput: input,
        },
        refetchQueries: buildRefetchQueries(),
      });
      toast.success("Activité modifiée avec succès");
      onSuccess();
    } catch (error) {
      console.error("Error updating activity:", error);
      toast.error(
        "Une erreur est survenue lors de la modification de l'activité"
      );
      throw error;
    }
  };

  const handleDelete = async (activityId: string) => {
    const response = confirm(
      "Étes-vous sûr de vouloir supprimer cette activité ? Tous les documents associés seront également supprimés."
    );

    if (!response) return;

    try {
      await deleteActivity({
        variables: {
          activityId,
        },
        refetchQueries: buildRefetchQueries(),
      });
      toast.success("Activité supprimée avec succès");
      onSuccess();
    } catch (error) {
      console.error("Error deleting activity:", error);
      toast.error(
        "Une erreur est survenue lors de la suppression de l'activité"
      );
      throw error;
    }
  };

  const uploadDocument = async (variables: { bucketKey: string }) => {
    const result = await uploadDocuments({
      variables,
    });
    return {
      data: result.data,
    };
  };

  return {
    isSubmitting,
    handleCreate,
    handleUpdate,
    handleDelete,
    uploadDocuments: uploadDocument,
  };
}
